import React, { useState, useEffect } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import SearchInput from '../../shared/components/student/SearchInput';
import { Constants, Blue_Border, BOX_SHADOW } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { useGetOrderHistory } from '../../assets/nodeApi/getApi/getCommonApi';
import CustomPagination from '../../shared/utils/CustomPagination';

const OrderSkeleton = () => (
    <Box
        sx={{
            border: Blue_Border,
            borderRadius: '15px',
            boxShadow: BOX_SHADOW,
            bgcolor: '#FFFFFF',
            overflow: 'hidden',
            p: { xs: 2.5, sm: 3 },
            mb: 3,
            maxWidth: '100%',
            boxSizing: 'border-box',
        }}
    >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, pb: 2, maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, md: 5 }, maxWidth: '100%' }}>
                <Box>
                    <Skeleton variant="text" width={60} height={15} />
                    <Skeleton variant="text" width={100} height={20} />
                </Box>
                <Box>
                    <Skeleton variant="text" width={40} height={15} />
                    <Skeleton variant="text" width={80} height={20} />
                </Box>
                <Box>
                    <Skeleton variant="text" width={40} height={15} />
                    <Skeleton variant="text" width={60} height={20} />
                </Box>
            </Box>
        </Box>
        <Box sx={{ borderBottom: '1px solid #E2E8F0', mb: 2 }} />
        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', gap: 3, overflowX: 'auto', pb: 1.5 }}>
            {[...Array(2)].map((_, i) => (
                <Box key={i} sx={{ width: '180px', flexShrink: 0 }}>
                    <Skeleton variant="rounded" width="100%" height={180} sx={{ mb: 1, borderRadius: '15px' }} />
                    <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={15} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="50%" height={20} />
                </Box>
            ))}
        </Box>
    </Box>
);

function OrderHistoryPage() {
    const { showToaster } = useToaster();
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    // Fetch orders history using API hook
    const { data: orderHistoryResponse, isLoading, isError, error } = useGetOrderHistory(
        paginationModel.page + 1,
        paginationModel.pageSize,
        debouncedSearchQuery
    );

    const handleProductClick = (itemName) => {
        showToaster(
            `Navigating to product details for "${itemName}"`,
            'Product View',
            'info'
        );
    };

    const orders = Array.isArray(orderHistoryResponse?.data)
        ? orderHistoryResponse.data
        : (Array.isArray(orderHistoryResponse) ? orderHistoryResponse : []);

    const totalRows = orderHistoryResponse?.pagination?.totalRecords ??
        orderHistoryResponse?.pagination?.total ??
        orderHistoryResponse?.pagination?.total_items ??
        orderHistoryResponse?.totalRecords ??
        orderHistoryResponse?.total ??
        orderHistoryResponse?.totalRows ??
        orderHistoryResponse?.count ??
        ((orderHistoryResponse?.pagination?.totalPages ??
            orderHistoryResponse?.pagination?.last_page ??
            orderHistoryResponse?.pagination?.total_pages ??
            orderHistoryResponse?.totalPages ??
            orderHistoryResponse?.last_page ??
            orderHistoryResponse?.total_pages ??
            1) * paginationModel.pageSize);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', maxWidth: '100%', overflowX: 'hidden', p: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>

            {/* Header Section */}
            <Box sx={{ maxWidth: '100%' }}>
                <Typography variant="h4" fontWeight={600} color="text.primary">
                    Order History
                </Typography>
                <Typography variant="h6" color="secondary.main" sx={{ mt: 0.5 }}>
                    Manage your recent purchase and track your shipment
                </Typography>
            </Box>

            {/* Search Toolbar */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', sm: '50%' }, maxWidth: '100%' }}>
                <SearchInput onChange={(val) => setSearchQuery(val)} placeholder="Search for anything..." />
            </Box>

            {/* Orders List Container */}
            <Box sx={{ mt: 1, maxWidth: '100%' }}>
                {isLoading ? (
                    [...Array(3)].map((_, i) => <OrderSkeleton key={i} />)
                ) : isError ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="error">
                            Failed to load orders: {error?.message || "Something went wrong"}
                        </Typography>
                    </Box>
                ) : orders.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Icon icon="solar:box-broken" width="64" height="64" style={{ color: '#A0AEC0', marginBottom: '16px' }} />
                        <Typography variant="h6" color="secondary.light">
                            No orders found
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {orders.map((order) => {
                            const orderNumber = order.order_number || `ORD-${order.id}`;
                            const formattedDate = order.created_at
                                ? new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                : 'N/A';
                            const totalAmount = parseFloat(order.total_amount) || 0;
                            const name = order.address?.full_name || 'N/A';
                            const phone = order.address?.phone || 'N/A';
                            const addressText = order.address
                                ? [
                                    order.address.address_line_1,
                                    order.address.address_line_2,
                                    order.address.city,
                                    order.address.state,
                                    order.address.postal_code,
                                    order.address.country,
                                ]
                                    .filter(Boolean)
                                    .join(', ')
                                : 'N/A';
                            const items = order.PurchaseItem || [];

                            return (
                                <Box
                                    key={order.id}
                                    sx={{
                                        border: Blue_Border,
                                        borderRadius: '15px',
                                        boxShadow: BOX_SHADOW,
                                        bgcolor: '#FFFFFF',
                                        overflow: 'hidden',
                                        p: { xs: 2.5, sm: 3 },
                                        mb: 3,
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0px 12px 24px 0px #4169E126',
                                        }
                                    }}
                                >
                                    {/* Order Meta Header Info */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 2,
                                        pb: 2,
                                        maxWidth: '100%'
                                    }}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, md: 5 }, maxWidth: '100%' }}>
                                            <Box>
                                                <Typography variant="body2" color="secondary.light" sx={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' }}>
                                                    Order ID
                                                </Typography>
                                                <Typography variant="body1" fontWeight={700} color="text.primary">
                                                    {orderNumber}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="secondary.light" sx={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' }}>
                                                    Date
                                                </Typography>
                                                <Typography variant="body1" fontWeight={500} color="text.primary">
                                                    {formattedDate}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="secondary.light" sx={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' }}>
                                                    Total
                                                </Typography>
                                                <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Icon icon="ph:currency-inr-bold" width="14" height="14" />
                                                    {totalAmount.toFixed(2)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Thin Divider Line */}
                                    <Box sx={{ borderBottom: '1px solid #E2E8F0', mb: 2 }} />

                                    {/* Order Address Info */}
                                    <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: '20px', mb: 1.5 }}>Shipping Details : </Typography>
                                    <Typography variant="body2" sx={{ mb: 1, color: '#4A5568', lineHeight: 1.5, wordBreak: 'break-word' }}>
                                        <Box component="span" sx={{ fontWeight: 500, color: 'text.primary', mr: 1 }}>
                                            Receiver Name :
                                        </Box>
                                        {name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1, color: '#4A5568', lineHeight: 1.5, wordBreak: 'break-word' }}>
                                        <Box component="span" sx={{ fontWeight: 500, color: 'text.primary', mr: 1 }}>
                                            Phone Number :
                                        </Box>
                                        {phone}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 3, color: '#4A5568', lineHeight: 1.5, wordBreak: 'break-word' }}>
                                        <Box component="span" sx={{ fontWeight: 500, color: 'text.primary', mr: 1 }}>
                                            Shipping Address :
                                        </Box>
                                        {addressText}
                                    </Typography>

                                    {/* Thin Divider Line */}
                                    <Box sx={{ borderBottom: '1px solid #E2E8F0', mb: 2 }} />

                                    {/* Order Items List */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'nowrap',
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            gap: 3,
                                            pb: 1.5,
                                            maxWidth: '100%',
                                            boxSizing: 'border-box',
                                            '&::-webkit-scrollbar': {
                                                height: '6px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#F1F1F1',
                                                borderRadius: '10px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#C7D5FF',
                                                borderRadius: '10px',
                                                '&:hover': {
                                                    backgroundColor: '#A9B7FD',
                                                }
                                            }
                                        }}
                                    >
                                        {items.map((purchaseItem, index) => {
                                            const itemName = purchaseItem.product?.title || 'Product Name';
                                            const itemQty = purchaseItem.quantity || 0;
                                            const itemPrice = parseFloat(purchaseItem.price) || 0;
                                            const itemImage = purchaseItem.product?.images?.[0] || Constants.customImages.DefaultImage;

                                            return (
                                                <Box
                                                    key={index}
                                                    onClick={() => handleProductClick(itemName)}
                                                    sx={{
                                                        width: '180px',
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        cursor: 'pointer',
                                                        boxSizing: 'border-box',
                                                        '&:hover img': {
                                                            transform: 'scale(1.05)',
                                                        }
                                                    }}
                                                >
                                                    {/* Item Image Box Container */}
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: '100%',
                                                            height: '180px',
                                                            bgcolor: '#F4F6FB',
                                                            borderRadius: '15px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            overflow: 'hidden',
                                                            p: 2,
                                                            mb: 1,
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        <img
                                                            src={itemImage}
                                                            alt={itemName}
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '100%',
                                                                objectFit: 'contain',
                                                                transition: 'transform 0.2s ease-in-out'
                                                            }}
                                                        />
                                                    </Box>

                                                    {/* Item Text Info */}
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={600}
                                                        color="text.primary"
                                                        sx={{
                                                            lineHeight: 1.2,
                                                            mb: 0.5,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            width: '100%',
                                                            display: 'block',
                                                            position: 'relative',
                                                            '&:hover span': {
                                                                display: 'inline-block',
                                                                animation: 'scrollText 6s linear infinite',
                                                            },
                                                            '@keyframes scrollText': {
                                                                '0%': { transform: 'translateX(0%)' },
                                                                '100%': { transform: 'translateX(-100%)' }
                                                            }
                                                        }}
                                                    >
                                                        <span style={{ display: 'inline-block' }}>
                                                            {itemName}
                                                        </span>
                                                    </Typography>
                                                    <Typography variant="body2" color="secondary.light" sx={{ mb: 0.5 }}>
                                                        Qty: {itemQty}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Icon icon="ph:currency-inr-bold" width="14" height="14" />
                                                        {itemPrice.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            );
                        })}

                        {/* Pagination Model */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                            <CustomPagination
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                totalRows={totalRows}
                                pageSize={paginationModel.pageSize}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}

export default OrderHistoryPage;
